import type { ExternalCost, Invoice } from '@forecastify/shared';
import type { Translate } from '../../i18n';
import type { Crumb } from '../breadcrumb';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Where a cost and an invoice BELONG — ux-pass-2, 14 Oct.                   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * *"Also breadcrumb is missing in both places, fix it."* Both pages used to say `id · name` and
 * nothing about where the record lives, which is the question `breadcrumb.tsx` exists to answer.
 *
 * ⚠️ **The trail follows the record's LINK, not the route it was opened from.** A cost billed to a
 * line item lives under that deliverable, so its trail runs project → contract → line item; one
 * booked against a period runs project → contract → period; a project-level one stops at the
 * project. An invoice always bills a line item or a period, so its trail always reaches a contract.
 *
 * ⚠️ Every segment but the last is a link (`Crumb.onClick`) and the last is the page itself — the
 * convention `breadcrumb.tsx` rules on. A segment whose address is unknown (a deleted line item)
 * is plain text rather than a link to nowhere.
 */

type Navigate = ((path: string) => void) | undefined;

function head(t: Translate, projectId: string, projectName: string, go: Navigate): Crumb[] {
  return [
    { label: t('breadcrumb.home'), onClick: () => go?.('/home') },
    { label: t('title.projects'), onClick: () => go?.('/projects') },
    { label: projectName, onClick: () => go?.(`/projects/${projectId}`) },
  ];
}

/** The contract, then the line item or the period, when the record names one. */
function middle(
  record: {
    readonly contractIdentifier: string | null;
    readonly lineItemIdentifier: string | null;
    readonly occurrenceStartsOn: string | null;
  },
  /* 5 Nov 2026 · a cost's `contract`; an invoice's `project` (`invoice_target`). */
  kind: 'line_item' | 'occurrence' | 'contract' | 'project',
  lineItemLabel: string | null,
  periodLabel: string | null,
  go: Navigate,
): Crumb[] {
  const contract = record.contractIdentifier;
  if (kind === 'project' || contract === null) return [];
  const crumbs: Crumb[] = [{ label: contract, onClick: () => go?.(`/contracts/${contract}`) }];
  if (kind === 'line_item' && lineItemLabel !== null) {
    const item = record.lineItemIdentifier;
    crumbs.push(
      item === null
        ? { label: lineItemLabel }
        : { label: lineItemLabel, onClick: () => go?.(`/contracts/${contract}/${item}`) },
    );
  }
  if (kind === 'occurrence' && record.occurrenceStartsOn !== null) {
    const startsOn = record.occurrenceStartsOn;
    crumbs.push({
      label: periodLabel ?? startsOn,
      onClick: () => go?.(`/contracts/${contract}/periods/${startsOn}`),
    });
  }
  return crumbs;
}

export function costCrumbs(cost: ExternalCost, t: Translate, go: Navigate): Crumb[] {
  return [
    ...head(t, cost.projectId, cost.projectName, go),
    ...middle(cost, cost.linkKind, cost.lineItemName, cost.occurrenceStartsOn, go),
    { label: cost.name },
  ];
}

export function invoiceCrumbs(invoice: Invoice, t: Translate, go: Navigate): Crumb[] {
  return [
    ...head(t, invoice.projectId, invoice.projectName, go),
    ...middle(
      invoice,
      invoice.targetKind,
      invoice.lineItemName ?? t('invoice.target.gone'),
      invoice.occurrenceIndex === null ? null : `#${String(invoice.occurrenceIndex)}`,
      go,
    ),
    /* ⚠️ THEIR number, which is the invoice's title — `invoice-detail.tsx` says why. */
    { label: invoice.externalId },
  ];
}
